import { useCallback, useMemo, type DragEvent } from "react";
import {
  Background,
  Controls,
  MarkerType,
  MiniMap,
  ReactFlow,
  useReactFlow,
  type Connection,
  type Edge,
  type EdgeChange,
  type IsValidConnection,
  type NodeChange,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useResolvedTheme } from "@/lib/theme-store";
import type { EstadoDisenador } from "../../hooks/use-estado-disenador";
import type { EventoDelCatalogo } from "../../services/notificaciones-service";
import { avisosDeNotificacion } from "../../utils/avisos";
import { MIME_ARRASTRE, type ElementoArrastrado } from "./PaletaLateral";
import { TIPOS_DE_NODO, type NodoEventoFlow, type NodoNotificacionFlow } from "./nodos";

type NodoFlow = NodoEventoFlow | NodoNotificacionFlow;

/** Lienzo controlado: el estado vive en `useEstadoDisenador`; React Flow solo lo dibuja. */
export function LienzoFlujo({ disenador: d, catalogo }: { disenador: EstadoDisenador; catalogo: EventoDelCatalogo[] }) {
  const { screenToFlowPosition } = useReactFlow();
  const tema = useResolvedTheme();
  const { estado, seleccion } = d;
  const porCodigo = useMemo(() => new Map(catalogo.map((e) => [e.codigo, e])), [catalogo]);

  const nodos = useMemo<NodoFlow[]>(
    () => [
      ...estado.eventos.map<NodoEventoFlow>((e) => {
        const info = porCodigo.get(e.tipoEvento);
        return {
          id: e.clave,
          type: "evento",
          position: estado.posiciones[e.clave] ?? { x: 0, y: 0 },
          selected: seleccion?.tipo === "evento" && seleccion.clave === e.clave,
          data: { nombre: info?.nombre ?? e.tipoEvento, categoria: info?.categoria ?? "—", codigo: e.tipoEvento, enCatalogo: !!info },
        };
      }),
      ...estado.notificaciones.map<NodoNotificacionFlow>((n) => ({
        id: n.clave,
        type: "notificacion",
        position: estado.posiciones[n.clave] ?? { x: 440, y: 0 },
        selected: seleccion?.tipo === "notificacion" && seleccion.clave === n.clave,
        data: { notificacion: n, avisos: avisosDeNotificacion(n, estado) },
      })),
    ],
    [estado, seleccion, porCodigo],
  );

  const aristas = useMemo<Edge[]>(
    () =>
      estado.conexiones.map((c) => ({
        id: c.clave,
        source: c.eventoClave,
        target: c.notificacionClave,
        selected: seleccion?.tipo === "conexion" && seleccion.clave === c.clave,
        label: c.condicion && c.condicion.reglas.length > 0 ? `si ${c.condicion.reglas.length} condición(es)` : undefined,
        animated: c.activa,
        style: c.activa ? undefined : { strokeDasharray: "6 4", opacity: 0.5 },
        markerEnd: { type: MarkerType.ArrowClosed },
      })),
    [estado.conexiones, seleccion],
  );

  const onNodesChange = useCallback(
    (cambios: NodeChange<NodoFlow>[]) => {
      for (const c of cambios) {
        if (c.type === "position" && c.position) d.moverNodo(c.id, c.position);
        else if (c.type === "remove") d.quitar(c.id);
        else if (c.type === "select" && c.selected) {
          const esEvento = estado.eventos.some((e) => e.clave === c.id);
          d.setSeleccion({ tipo: esEvento ? "evento" : "notificacion", clave: c.id });
        }
      }
    },
    [d, estado.eventos],
  );

  const onEdgesChange = useCallback(
    (cambios: EdgeChange[]) => {
      for (const c of cambios) {
        if (c.type === "remove") d.quitar(c.id);
        else if (c.type === "select" && c.selected) d.setSeleccion({ tipo: "conexion", clave: c.id });
      }
    },
    [d],
  );

  const esValida = useCallback<IsValidConnection>(
    (c) =>
      estado.eventos.some((e) => e.clave === c.source) &&
      estado.notificaciones.some((n) => n.clave === c.target) &&
      !estado.conexiones.some((x) => x.eventoClave === c.source && x.notificacionClave === c.target),
    [estado],
  );

  const onConnect = useCallback((c: Connection) => d.unir(c.source, c.target), [d]);

  const onDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      const crudo = e.dataTransfer.getData(MIME_ARRASTRE);
      if (!crudo) return;
      const elemento = JSON.parse(crudo) as ElementoArrastrado;
      const posicion = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      if (elemento.tipo === "evento") d.agregarEvento(elemento.codigo, posicion);
      else d.agregarNotificacion(elemento.canal, posicion);
    },
    [d, screenToFlowPosition],
  );

  return (
    <div className="relative min-h-0" onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }} onDrop={onDrop}>
      <ReactFlow<NodoFlow, Edge>
        nodes={nodos}
        edges={aristas}
        nodeTypes={TIPOS_DE_NODO}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        isValidConnection={esValida}
        onNodeClick={(_, n) => d.setSeleccion({ tipo: n.type === "evento" ? "evento" : "notificacion", clave: n.id })}
        onEdgeClick={(_, e) => d.setSeleccion({ tipo: "conexion", clave: e.id })}
        onPaneClick={() => d.setSeleccion(null)}
        colorMode={tema}
        fitView
        fitViewOptions={{ padding: 0.3, maxZoom: 1 }}
        deleteKeyCode={["Backspace", "Delete"]}
        proOptions={{ hideAttribution: true }}
        className="[--xy-background-color:var(--color-card)] [--xy-node-background-color:var(--color-card)]"
      >
        <Background gap={20} />
        <Controls showInteractive={false} />
        <MiniMap pannable zoomable className="!hidden lg:!block" />
      </ReactFlow>
      {estado.eventos.length === 0 && estado.notificaciones.length === 0 ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-8 text-center text-sm text-muted-foreground">
          Arrastra un evento desde la izquierda y luego un canal (correo, webhook o campana).
          <br />
          Une el punto del evento con la notificación para ligarlos.
        </div>
      ) : null}
    </div>
  );
}
